import { Check, TriangleAlert, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '~/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table';
import { formatUtcTimestampPrecise } from '~/utils/date';

import { useWorkflowWorkers } from '../../hooks/use-workflow-inspection';
import { SectionError } from '../section-error';

/**
 * Workers (§3.6), laid out like Temporal's Workers tab: a count, the task
 * queue, and one row per worker (identity masked unless revealed) with when it
 * last polled and ✓/✗ for whether it handles workflow tasks and/or activities.
 * No workers at all is flagged — nothing can pick the run's work up.
 */
export function WorkersTab({ workflowId, runId }: { workflowId: string; runId: string | null }) {
  const { t } = useTranslation('workflow-inspection');
  const workers = useWorkflowWorkers(workflowId, runId);

  if (workers.isLoading)
    return <Skeleton className="h-40 w-full" data-testid="workflow-inspector-workers-loading" />;
  if (workers.isError || !workers.data)
    return (
      <SectionError
        onRetry={() => void workers.refetch()}
        isRetrying={workers.isFetching}
        testId="workflow-inspector-workers-error"
      />
    );

  const { taskQueue, workers: rows } = workers.data;

  return (
    <div className="space-y-3" data-testid="workflow-inspector-workers">
      <div className="flex items-center gap-2">
        <h3 className="text-foreground text-base font-semibold">{t('workers.title')}</h3>
        <span
          data-testid="workflow-inspector-workers-count-value"
          className="bg-primary/10 text-primary inline-flex min-w-6 items-center justify-center rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums"
        >
          {rows.length}
        </span>
      </div>
      {taskQueue && (
        <p className="text-sm">
          <span className="text-muted-foreground">{t('workers.taskQueue')}: </span>
          <span
            className="text-foreground font-mono text-xs"
            data-testid="workflow-inspector-workers-task-queue-value"
          >
            {taskQueue}
          </span>
        </p>
      )}

      {rows.length === 0 ? (
        <p
          className="bg-warning/10 text-foreground flex items-center gap-2 rounded-md px-3 py-2 text-sm"
          data-testid="workflow-inspector-workers-empty"
        >
          <TriangleAlert aria-hidden="true" className="text-warning size-4 shrink-0" />
          {t('workers.none')}
        </p>
      ) : (
        <div className="bg-background rounded-lg border">
          <Table data-testid="workflow-inspector-workers-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t('workers.id')}</TableHead>
                <TableHead className="w-60">{t('workers.lastAccessed')}</TableHead>
                <TableHead className="w-48 text-center">
                  {t('workers.workflowTaskHandler')}
                </TableHead>
                <TableHead className="w-40 text-center">{t('workers.activityHandler')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((w, i) => (
                // Index key: masked identities can collide (same host prefix).
                <TableRow key={i} data-testid="workflow-inspector-workers-row">
                  <TableCell className="font-mono text-xs break-all whitespace-normal">
                    {w.identity}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs tabular-nums">
                    {w.lastAccessTime ? formatUtcTimestampPrecise(w.lastAccessTime) : '—'}
                  </TableCell>
                  <TableCell className="text-center">
                    <HandlerMark handles={w.workflowTaskHandler} />
                  </TableCell>
                  <TableCell className="text-center">
                    <HandlerMark handles={w.activityHandler} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

/** ✓ (primary) when the worker polls for this task kind, ✗ (muted) when not. */
function HandlerMark({ handles }: { handles: boolean }) {
  const { t } = useTranslation('workflow-inspection');
  const label = handles ? t('workers.handles') : t('workers.doesNotHandle');
  return handles ? (
    <Check aria-label={label} className="text-primary inline size-4" />
  ) : (
    <X aria-label={label} className="text-muted-foreground inline size-4" />
  );
}
